import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import { Button, CircularProgress, Dialog, Typography } from '@mui/material';

import { documentKindOf } from './documentKind';
import { directUrlOf, type DocumentSource } from './documentSource';
import { ImageViewerDialog } from './ImageViewerDialog';
import { PdfViewerDialog } from './PdfViewerDialog';
import { useDocumentObjectUrl } from './useDocumentObjectUrl';

interface DocumentViewerDialogProps {
  open: boolean;
  onClose: () => void;
  /** Where the document's bytes come from. */
  source: DocumentSource;
  /** Names the document in the header and to a screen reader. */
  title: string;
  /** The MIME type the server committed to, when the caller knows it. Trusted
   *  over the URL's extension unless it is the `octet-stream` non-answer. */
  contentType?: string;
}

/**
 * A document, read in the portal, whatever kind of file it turns out to be.
 *
 * The portal used to hand every document straight to the browser as a link,
 * which for a presigned S3 URL served `Content-Disposition: attachment` meant a
 * download — on a phone, that is leaving the portal, finding the file in Files,
 * and coming back. Directors are asked to sign statutory documents here;
 * reading one should not cost them the flow they were in.
 *
 * The kind of file decides the viewer, because the feed is not all PDFs — a few
 * hundred documents are scans, photos and Office attachments. A PDF is rendered
 * page by page by pdf.js, an image goes to the pinch-and-zoom viewer, and
 * anything a browser genuinely cannot render says so and offers the tab, rather
 * than opening a frame that was never going to paint.
 *
 * The dispatch happens here rather than at each call site so that "open a
 * document" is one thing everywhere in the app, and so a new file kind is
 * handled in one file rather than in every screen that lists documents.
 */
export const DocumentViewerDialog = ({
  open,
  onClose,
  source,
  title,
  contentType,
}: DocumentViewerDialogProps) => {
  const kind = documentKindOf(directUrlOf(source), contentType);

  if (kind === 'image') {
    return <ImageDocument open={open} onClose={onClose} source={source} title={title} />;
  }

  if (kind === 'unsupported') {
    return <UnsupportedDocument open={open} onClose={onClose} source={source} title={title} />;
  }

  return <PdfViewerDialog open={open} onClose={onClose} source={source} title={title} />;
};

interface KindProps {
  open: boolean;
  onClose: () => void;
  source: DocumentSource;
  title: string;
}

/**
 * An image document, fetched before it is shown.
 *
 * An `<img src>` pointing at the documents bucket would load — an image tag is
 * not governed by CORS the way `fetch` is — but it would be the only thing in
 * the app reaching the bucket directly, and it would break the moment a
 * document needs the session token to fetch. Going through the same transfer as
 * every other document keeps one rule rather than two.
 */
const ImageDocument = ({ open, onClose, source, title }: KindProps) => {
  const image = useDocumentObjectUrl(source, open);

  if (image.status === 'error') {
    return <UnsupportedDocument open={open} onClose={onClose} source={source} title={title} />;
  }

  // The zoom viewer needs a src to render at all, so the wait gets a dialog of
  // its own rather than nothing: a tap that paints no dialog for the length of
  // a mobile fetch reads as a dead button, and leaves nothing to close.
  if (image.status !== 'ready') {
    return <LoadingDocument open={open} onClose={onClose} title={title} />;
  }

  return (
    <ImageViewerDialog
      open={open}
      onClose={onClose}
      src={image.url}
      alt={title}
      title={title}
      onPlate
    />
  );
};

/**
 * The wait, while an image's bytes are still in flight.
 *
 * The same shell the unsupported state uses, so the dialog does not change
 * shape underneath the reader when the answer arrives — and Close is reachable
 * throughout, which is the part a null render took away.
 */
const LoadingDocument = ({ open, onClose, title }: Omit<KindProps, 'source'>) => (
  <Dialog
    open={open}
    onClose={onClose}
    slotProps={{ paper: { className: 'm-4 rounded-2xl', 'aria-label': title } }}
  >
    <div className="flex flex-col items-center gap-4 px-6 py-8 text-center">
      <Typography variant="subtitle2" component="p">
        {title}
      </Typography>

      <div className="flex items-center gap-3" role="status">
        <CircularProgress size={20} />
        <Typography variant="body2" className="text-content-muted">
          Loading {title}…
        </Typography>
      </div>

      <Button onClick={onClose}>Close</Button>
    </div>
  </Dialog>
);

/**
 * The honest answer for a Word document, an Outlook message, or a file that
 * would not load.
 *
 * A dialog rather than silently falling back to a download, because the reader
 * tapped "View" and deserves to be told what happened to it.
 */
const UnsupportedDocument = ({ open, onClose, source, title }: KindProps) => {
  const directUrl = directUrlOf(source);

  // The label goes on the PAPER, not on the Dialog: MUI spreads a Dialog's own
  // props onto the outer `role="presentation"` wrapper, so `aria-label` there
  // never reaches the `role="dialog"` element and leaves it unnamed.
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{ paper: { className: 'm-4 rounded-2xl', 'aria-label': title } }}
    >
      <div className="flex flex-col items-center gap-4 px-6 py-8 text-center">
        {/* A paragraph, not the `<h6>` `subtitle2` maps to: the dialog is
            already named by this same text, and a heading would repeat it. */}
        <Typography variant="subtitle2" component="p">
          {title}
        </Typography>

        <Typography variant="body2" className="text-content-muted" role="status">
          {directUrl === ''
            ? 'This file can’t be shown in the portal.'
            : 'This file type can’t be shown in the portal. Open it in a new tab to read it on your device.'}
        </Typography>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {directUrl !== '' && (
            <Button
              component="a"
              href={directUrl}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              endIcon={<OpenInNewIcon />}
            >
              Open in new tab
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </Dialog>
  );
};
